import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LogIn, Loader2, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { base44 } from '@/api/base44Client';

export default function SignInForm({ onBack, onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleForgotPassword = async () => {
    setResetError('');
    if (!email) { setResetError('Enter your email address above first.'); return; }
    setResetLoading(true);
    try {
      await base44.auth.resetPasswordRequest(email);
      setResetSent(true);
    } catch {
      setResetError('Could not send reset email. Please check your email address.');
    }
    setResetLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      await base44.auth.loginViaEmailPassword(email, password);
      // Claim any pending admin invitation for this email (best-effort). If promoted,
      // re-login so the session token carries the admin role (otherwise admin pages
      // see the stale role and show "Access Denied").
      let promoted = false;
      try { const r = await base44.functions.invoke('claimAdminInvite', {}); promoted = !!r.data?.promoted; } catch (e) { /* ignore */ }
      if (promoted) {
        try { await base44.auth.loginViaEmailPassword(email, password); } catch (e) { /* ignore */ }
      }
      onSuccess();
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
      <div className="w-full max-w-md px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>

          {/* Logo */}
          <div className="flex justify-center mb-10">
            <img
              src="https://media.base44.com/images/public/69e7659143303e8153678f84/d1f090528_Asset1.png"
              alt="Motion Reality"
              className="h-10 w-auto"
            />
          </div>

          <p className="text-primary text-xs font-bold tracking-widest mb-2 text-center">CUSTOMER PORTAL</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase text-center mb-8">SIGN IN</h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">EMAIL ADDRESS</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <Label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">PASSWORD</Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60 pr-10"
                  placeholder="Your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 px-4 py-3">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-bold text-sm py-4 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              {loading ? 'SIGNING IN...' : 'SIGN IN'}
            </button>

            <button
              type="button"
              onClick={onBack}
              className="w-full border border-white/10 hover:border-white/25 text-white/40 hover:text-white font-bold text-sm py-3 transition-all"
            >
              BACK
            </button>
          </form>

          <div className="text-center mt-6 space-y-1">
            {resetSent ? (
              <p className="text-green-400 text-xs">Reset email sent to <span className="font-bold">{email}</span>. Check your inbox.</p>
            ) : (
              <p className="text-white/20 text-xs">
                Forgot your password?{' '}
                <button type="button" onClick={handleForgotPassword} disabled={resetLoading}
                  className="text-primary hover:underline disabled:opacity-50">
                  {resetLoading ? 'Sending...' : 'Reset it here'}
                </button>
              </p>
            )}
            {resetError && <p className="text-red-400 text-xs">{resetError}</p>}
          </div>
        </motion.div>
      </div>
    </div>
  );
}